import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export function Modal({ open, onClose, title, children, size = "md" }) {
    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);
    if (!open) return null;
    const w = size === "lg" ? "max-w-3xl" : size === "xl" ? "max-w-5xl" : "max-w-xl";
    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="modal-overlay">
            <div className="min-h-full flex items-center justify-center p-4">
                <div className={`w-full ${w} my-4 rounded-2xl bg-navy-900 border border-gold-500/30 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
                    <div className="sticky top-0 bg-navy-900 z-10 px-6 py-4 border-b border-white/5 flex items-center justify-between rounded-t-2xl">
                        <h3 className="font-heading text-xl font-bold text-white">{title}</h3>
                        <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="modal-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-6">{children}</div>
                </div>
            </div>
        </div>,
        document.body
    );
}

/* ------------------- USER FORM ------------------- */
const ROLES = [
    { v: "buyer", l: "Buyer / Tenant" },
    { v: "seller", l: "Seller / Owner" },
    { v: "broker", l: "Broker" },
    { v: "advocate", l: "Advocate" },
    { v: "loan_agent", l: "Loan Agent" },
    { v: "architect", l: "Architect" },
    { v: "interior_designer", l: "Interior Designer" },
    { v: "civil_contractor", l: "Civil Contractor" },
    { v: "user", l: "Legacy user" },
];

export function UserFormModal({ open, onClose, user, onSaved }) {
    const isEdit = !!user;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm({
            name: user?.name || "",
            email: user?.email || "",
            password: "",
            phone: user?.phone || "",
            role: user?.role || "broker",
            city: user?.city || "",
            bio: user?.bio || "",
            avatar_url: user?.avatar_url || "",
            rera_number: user?.rera_number || "",
            bar_council_id: user?.bar_council_id || "",
            experience_years: user?.experience_years || "",
            verified: user?.verified || false,
        });
    }, [user, open]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.experience_years === "") payload.experience_years = null;
            else payload.experience_years = Number(payload.experience_years);
            if (isEdit) {
                if (!payload.password) delete payload.password;
                delete payload.email; // email not editable
                await api.put(`/admin/users/${user.id}`, payload);
                toast.success("User updated");
            } else {
                if (!payload.password) {
                    toast.error("Password required");
                    setSaving(false);
                    return;
                }
                await api.post("/admin/users", payload);
                toast.success("User created");
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title={isEdit ? `Edit · ${user.name}` : "Add Partner / User"} size="lg">
            <form onSubmit={submit} className="space-y-4" data-testid="user-form">
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="uf-name" />
                    <Select label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="uf-role" options={ROLES} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Email" type="email" required disabled={isEdit} value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="uf-email" />
                    <Field label={isEdit ? "Reset password (optional)" : "Password"} type="password" required={!isEdit} value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="uf-password" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="uf-phone" />
                    <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="uf-city" />
                </div>
                <Field label="Avatar URL" value={form.avatar_url} onChange={(v) => setForm({ ...form, avatar_url: v })} testid="uf-avatar" placeholder="https://…" />
                <div className="grid grid-cols-3 gap-3">
                    <Field label="RERA #" value={form.rera_number} onChange={(v) => setForm({ ...form, rera_number: v })} testid="uf-rera" />
                    <Field label="Bar Council ID" value={form.bar_council_id} onChange={(v) => setForm({ ...form, bar_council_id: v })} testid="uf-bar" />
                    <Field label="Experience (years)" type="number" value={form.experience_years} onChange={(v) => setForm({ ...form, experience_years: v })} testid="uf-exp" />
                </div>
                <Field label="Bio" textarea value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} testid="uf-bio" />
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                    <input type="checkbox" checked={!!form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} className="accent-gold-500 w-4 h-4" data-testid="uf-verified" />
                    <span>Mark as <strong className="text-gold-500">Verified</strong> partner</span>
                </label>
                <div className="flex gap-3 pt-2 border-t border-white/5">
                    <button type="submit" disabled={saving} data-testid="uf-submit" className="flex-1 px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-60">
                        {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create User")}
                    </button>
                    <button type="button" onClick={onClose} className="px-5 py-3 rounded-md border border-white/15 text-white/80 hover:border-white/30">Cancel</button>
                </div>
            </form>
        </Modal>
    );
}

/* ------------------- PROPERTY FORM ------------------- */
const AMENITY_OPTS = ["Swimming Pool", "Gym", "24/7 Security", "Power Backup", "Clubhouse", "Garden", "Parking", "Lift", "Furnished", "Pet Friendly", "Solar Power", "Smart Home", "Sea View", "Servant Quarter"];

export function PropertyFormModal({ open, onClose, property, owners = [], onSaved, onSwitchToAuction }) {
    const isEdit = !!property;
    const [form, setForm] = useState({ amenities: [], images: [] });
    const [ownerId, setOwnerId] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        setForm({
            title: property?.title || "",
            description: property?.description || "",
            listing_type: property?.listing_type || "sale",
            property_type: property?.property_type || "residential",
            subtype: property?.subtype || "",
            price: property?.price || "",
            bhk: property?.bhk || "",
            bathrooms: property?.bathrooms || "",
            area_sqft: property?.area_sqft || "",
            city: property?.city || "",
            state: property?.state || "Maharashtra",
            locality: property?.locality || "",
            address: property?.address || "",
            pincode: property?.pincode || "",
            contact_phone: property?.contact_phone || "",
            amenities: property?.amenities || [],
            images: property?.images || [],
            featured: property?.featured || false,
            latitude: property?.latitude || "",
            longitude: property?.longitude || "",
        });
        setOwnerId(property?.owner_id || (owners[0]?.id || ""));
    }, [property, open, owners]);

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/properties/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(data.url);
            }
            setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...urls] }));
            toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                bhk: form.bhk === "" ? null : Number(form.bhk),
                bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
                area_sqft: form.area_sqft === "" ? null : Number(form.area_sqft),
                latitude: form.latitude === "" ? null : Number(form.latitude),
                longitude: form.longitude === "" ? null : Number(form.longitude),
            };
            if (isEdit) {
                await api.put(`/admin/properties/${property.id}`, payload);
                toast.success("Property updated");
            } else {
                await api.post("/admin/properties", payload, { params: { owner_id: ownerId } });
                toast.success("Property created");
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const toggleAmenity = (a) => setForm((f) => ({
        ...f,
        amenities: (f.amenities || []).includes(a) ? f.amenities.filter((x) => x !== a) : [...(f.amenities || []), a],
    }));

    return (
        <Modal open={open} onClose={onClose} title={isEdit ? `Edit · ${property.title}` : "Add New Property"} size="xl">
            <form onSubmit={submit} className="space-y-5" data-testid="property-form">
                {!isEdit && (
                    <>
                        {/* Listing kind tabs: Sale / Rent / Auction */}
                        <div>
                            <label className="text-xs text-white/60 mb-2 block">Listing kind</label>
                            <div className="grid grid-cols-3 gap-2" data-testid="pf-kind-tabs">
                                {[
                                    { v: "sale", l: "For Sale" },
                                    { v: "rent", l: "For Rent" },
                                    { v: "auction", l: "Auction" },
                                ].map((t) => {
                                    const active = t.v === "auction" ? false : form.listing_type === t.v;
                                    return (
                                        <button
                                            key={t.v}
                                            type="button"
                                            data-testid={`pf-kind-${t.v}`}
                                            onClick={() => {
                                                if (t.v === "auction") {
                                                    if (onSwitchToAuction) {
                                                        onClose();
                                                        onSwitchToAuction();
                                                    }
                                                } else {
                                                    setForm({ ...form, listing_type: t.v });
                                                }
                                            }}
                                            className={`px-3 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                                                active
                                                    ? "border-gold-500 bg-gold-500/15 text-gold-300"
                                                    : "border-white/10 bg-navy-950 text-white/70 hover:border-gold-500/40 hover:text-white"
                                            }`}
                                        >
                                            {t.l}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-white/45 mt-1.5">Auction listings have a separate form with base price, bid increment and timings.</p>
                        </div>

                        <div>
                            <label className="text-xs text-white/60 mb-1 block">Owner / Listing Agent</label>
                            <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="pf-owner">
                                {owners.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.role} ({o.email})</option>)}
                            </select>
                        </div>
                    </>
                )}
                <Field label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="pf-title" />
                <Field label="Description" required textarea value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="pf-desc" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Select label="For" value={form.listing_type} onChange={(v) => setForm({ ...form, listing_type: v })} testid="pf-listing"
                        options={[{ v: "sale", l: "Sale" }, { v: "rent", l: "Rent" }]} />
                    <Select label="Type" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })} testid="pf-type"
                        options={[{ v: "residential", l: "Residential" }, { v: "commercial", l: "Commercial" }, { v: "plot", l: "Plot" }, { v: "farmland", l: "Farmland" }]} />
                    <Field label="Subtype" value={form.subtype} onChange={(v) => setForm({ ...form, subtype: v })} testid="pf-subtype" placeholder="e.g. apartment" />
                    <Field label="Price (₹)" required type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="pf-price" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="BHK" type="number" value={form.bhk} onChange={(v) => setForm({ ...form, bhk: v })} testid="pf-bhk" />
                    <Field label="Bathrooms" type="number" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testid="pf-bath" />
                    <Field label="Area (sqft)" type="number" value={form.area_sqft} onChange={(v) => setForm({ ...form, area_sqft: v })} testid="pf-area" />
                    <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="pf-phone" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="City" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="pf-city" />
                    <Field label="State" required value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="pf-state" />
                    <Field label="Locality" value={form.locality} onChange={(v) => setForm({ ...form, locality: v })} testid="pf-locality" />
                    <Field label="Pincode" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} testid="pf-pincode" />
                </div>
                <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="pf-address" />
                <div>
                    <label className="text-xs text-white/60 mb-2 block">Amenities</label>
                    <div className="flex flex-wrap gap-2">
                        {AMENITY_OPTS.map((a) => (
                            <button key={a} type="button" onClick={() => toggleAmenity(a)}
                                className={`px-3 py-1 rounded-full text-xs font-medium border ${(form.amenities || []).includes(a) ? "bg-gold-500 text-navy-900 border-gold-500" : "border-white/10 text-white/70 hover:border-gold-500/40"}`}
                            >{a}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-xs text-white/60 mb-2 block">Photos</label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {form.images?.map((img, i) => (
                            <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-navy-950 border border-white/10">
                                <img src={img.startsWith("http") ? img : (process.env.REACT_APP_BACKEND_URL + img)} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => setForm((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-navy-900/90 text-white hover:bg-red-500 flex items-center justify-center">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                        <label className="aspect-square rounded-md border-2 border-dashed border-white/15 hover:border-gold-500/60 cursor-pointer flex items-center justify-center text-white/60 text-xs">
                            {uploading ? "…" : "+ Upload"}
                            <input type="file" multiple accept="image/*" onChange={upload} className="hidden" data-testid="pf-image-input" />
                        </label>
                    </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                    <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-gold-500 w-4 h-4" data-testid="pf-featured" />
                    <span>Mark as <strong className="text-gold-500">Featured</strong> on home page</span>
                </label>
                <div className="flex gap-3 pt-2 border-t border-white/5">
                    <button type="submit" disabled={saving} data-testid="pf-submit" className="flex-1 px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-60">
                        {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Property")}
                    </button>
                    <button type="button" onClick={onClose} className="px-5 py-3 rounded-md border border-white/15 text-white/80 hover:border-white/30">Cancel</button>
                </div>
            </form>
        </Modal>
    );
}

/* ------------------- Helpers ------------------- */
function Field({ label, value, onChange, type = "text", required, testid, placeholder, textarea, disabled }) {
    return (
        <div>
            <label className="text-xs text-white/60 mb-1 block">{label}</label>
            {textarea ? (
                <textarea required={required} disabled={disabled} rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 disabled:opacity-50" />
            ) : (
                <input type={type} required={required} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 disabled:opacity-50" />
            )}
        </div>
    );
}

function Select({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="text-xs text-white/60 mb-1 block">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-navy-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </div>
    );
}
